import { useEffect, useState } from "react";
import {
  BellRing,
  Mail,
  Phone,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  Trash2,
} from "lucide-react";
import {
  createReminder,
  getReminderForUpload,
  clearRemindersForUpload,
} from "../lib/reminders";
import { formatApiError } from "../lib/authApi";

const CHANNELS = [
  { value: "email", label: "Email", icon: Mail },
  { value: "whatsapp", label: "WhatsApp", icon: Phone },
  { value: "both", label: "Both", icon: BellRing },
];

const DAYS = [7, 15, 30, 45];

/**
 * Renewal reminder capture card — sits on the analysis page under the
 * disclaimer. Currently CAPTURE-ONLY; the delivery worker (Resend + Twilio
 * WhatsApp) is a future integration.
 */
export default function RenewalReminderCard({ uploadId, policyEndDate }) {
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const [channel, setChannel] = useState("email");
  const [days, setDays] = useState(15);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");

  useEffect(() => {
    if (!uploadId) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await getReminderForUpload(uploadId);
        if (cancelled) return;
        if (d && d.id) {
          setExisting(d);
          setChannel(d.channel || "email");
          setDays(d.days_before || 15);
          setEmail(d.email || "");
          setPhone(d.phone || "");
          setName(d.name || "");
        }
      } catch (e) {
        // Non-fatal — just render the capture form.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uploadId]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const needsEmail = channel === "email" || channel === "both";
      const needsPhone = channel === "whatsapp" || channel === "both";
      if (needsEmail && !email.trim()) throw new Error("Please enter an email address.");
      if (needsPhone && !phone.trim()) throw new Error("Please enter a phone number.");

      const payload = {
        upload_id: uploadId,
        channel,
        days_before: Number(days),
        policy_end_date: policyEndDate && policyEndDate !== "not specified in your policy" ? policyEndDate : undefined,
        name: name.trim() || undefined,
      };
      if (needsEmail) payload.email = email.trim();
      if (needsPhone) payload.phone = phone.trim();

      const res = await createReminder(payload);
      setExisting({ ...payload, ...res });
      setShowEdit(false);
    } catch (err) {
      setError(err?.message || formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const clearAll = async () => {
    setBusy(true);
    try {
      await clearRemindersForUpload(uploadId);
      setExisting(null);
      setEmail("");
      setPhone("");
      setName("");
      setChannel("email");
      setDays(15);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!uploadId || loading) return null;

  const captured = !!existing && !showEdit;
  const channelLabel =
    existing?.channel === "both"
      ? "email & WhatsApp"
      : existing?.channel === "whatsapp"
      ? "WhatsApp"
      : "email";

  return (
    <div
      className="mt-6 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5 md:p-6"
      data-testid="renewal-reminder-card"
    >
      <div className="flex items-start gap-4">
        <div className="h-11 w-11 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0 shadow-sm shadow-amber-500/20">
          <BellRing className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          {captured ? (
            <>
              <h3
                className="font-display font-semibold text-slate-900 flex items-center gap-1.5"
                data-testid="renewal-reminder-captured-heading"
              >
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                Renewal reminder set
              </h3>
              <p className="mt-0.5 text-sm text-slate-600 leading-relaxed">
                We&apos;ll ping you on{" "}
                <strong className="text-slate-900">{channelLabel}</strong>
                {" "}
                <strong className="text-slate-900">{existing.days_before} days</strong>{" "}
                before this policy expires
                {existing.policy_end_date ? (
                  <> (<span className="tabular-nums">{existing.policy_end_date}</span>)</>
                ) : null}
                .
                {existing.email && (
                  <span className="block text-xs text-slate-500 mt-1">
                    Email: <code>{existing.email}</code>
                  </span>
                )}
                {existing.phone && (
                  <span className="block text-xs text-slate-500">
                    WhatsApp: <code>{existing.phone}</code>
                  </span>
                )}
              </p>
              <p className="mt-2 text-[11px] text-amber-800 bg-amber-100/60 rounded-md px-2 py-1 inline-block">
                Note: reminders will be sent once our email/WhatsApp integration is enabled.
                Your details are stored securely in the meantime.
              </p>
              <div className="mt-3 flex items-center gap-3 text-sm">
                <button
                  type="button"
                  onClick={() => setShowEdit(true)}
                  data-testid="renewal-reminder-edit"
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={clearAll}
                  disabled={busy}
                  data-testid="renewal-reminder-clear"
                  className="inline-flex items-center gap-1 text-slate-500 hover:text-red-600 disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Remove
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 className="font-display font-semibold text-slate-900">
                Get a renewal reminder
              </h3>
              <p className="mt-0.5 text-sm text-slate-600">
                We&apos;ll remind you before this policy expires so you never lose
                cover mid-drive.
                {policyEndDate && policyEndDate !== "not specified in your policy" && (
                  <> Policy ends <strong>{policyEndDate}</strong>.</>
                )}
              </p>

              <form onSubmit={submit} className="mt-4 space-y-3" data-testid="renewal-reminder-form">
                <div>
                  <div className="text-xs font-medium text-slate-500 mb-1.5">Remind me via</div>
                  <div className="flex flex-wrap gap-2">
                    {CHANNELS.map((c) => {
                      const Icon = c.icon;
                      const active = channel === c.value;
                      return (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => setChannel(c.value)}
                          data-testid={`renewal-channel-${c.value}`}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm ${
                            active
                              ? "bg-slate-900 border-slate-900 text-white"
                              : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                          }`}
                        >
                          <Icon className="h-3.5 w-3.5" />
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(channel === "email" || channel === "both") && (
                    <div>
                      <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                        Email
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        data-testid="renewal-email"
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                  )}
                  {(channel === "whatsapp" || channel === "both") && (
                    <div>
                      <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                        WhatsApp number
                      </label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+91 98xxxxxx98"
                        data-testid="renewal-phone"
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                      How early?
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {DAYS.map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => setDays(d)}
                          data-testid={`renewal-days-${d}`}
                          className={`rounded-full border px-3 py-1.5 text-sm ${
                            days === d
                              ? "bg-blue-600 border-blue-600 text-white"
                              : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"
                          }`}
                        >
                          {d} days
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                      Your name <span className="text-slate-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Priya"
                      maxLength={80}
                      data-testid="renewal-name"
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                    />
                  </div>
                </div>

                <p className="text-[11px] text-slate-500 leading-snug">
                  We&apos;ll send exactly one reminder per policy. Delivery goes live
                  once our email/WhatsApp integration is enabled — until then, we
                  store your request securely and never share it.
                </p>

                {error && (
                  <div
                    className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 flex items-start gap-2"
                    data-testid="renewal-error"
                  >
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="renewal-submit"
                    className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white rounded-full px-4 py-2 text-sm font-semibold"
                  >
                    {busy ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Saving…
                      </>
                    ) : (
                      <>
                        <BellRing className="h-3.5 w-3.5" />
                        {existing ? "Update reminder" : "Set reminder"}
                      </>
                    )}
                  </button>
                  {existing && (
                    <button
                      type="button"
                      onClick={() => setShowEdit(false)}
                      disabled={busy}
                      className="text-sm text-slate-500 hover:text-slate-900 rounded-full px-3 py-2 hover:bg-slate-100"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
